<template>
  <div class="relative flex-1 bg-gradient-to-br from-primary-50 to-purple-50 dark:from-neutral-900 dark:to-neutral-800">
    <div class="pointer-events-none absolute inset-0 bg-grid-pattern opacity-[0.04] dark:opacity-[0.06]" aria-hidden="true" />

    <!-- Controles superiores -->
    <div class="absolute top-3 left-3 sm:top-4 sm:left-4 z-20">
      <button
        type="button"
        class="p-2.5 rounded-xl bg-white/85 dark:bg-neutral-800/85 backdrop-blur-sm shadow-md border border-neutral-200/70 dark:border-neutral-700/70 hover:bg-white dark:hover:bg-neutral-800 transition-colors"
        :aria-label="t('install.toggle_theme')"
        @click="toggleTheme"
      >
        <svg v-if="isDark" class="w-5 h-5 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
        <svg v-else class="w-5 h-5 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      </button>
    </div>

    <div class="relative flex min-h-full items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
      <div class="w-full max-w-lg sm:max-w-xl lg:max-w-2xl">
        <div class="overflow-hidden rounded-2xl border border-neutral-200/50 dark:border-neutral-700/50 bg-white dark:bg-neutral-800 shadow-2xl">
          <!-- Header -->
          <div class="px-5 py-8 sm:px-8 sm:py-10 text-white text-center bg-gradient-to-r" :class="presentation.visual.header">
            <div class="mx-auto mb-4 flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm ring-1 ring-white/20">
              <img
                v-if="getLoginLogo && !logoFailed"
                :src="getLoginLogo"
                :alt="getPanelName"
                class="h-10 w-auto sm:h-12 object-contain"
                @error="logoFailed = true"
              />
              <component v-else :is="headerIcon" class="h-9 w-9 sm:h-10 sm:w-10" />
            </div>
            <h1 class="text-2xl sm:text-3xl font-bold tracking-tight">
              {{ t('license.invalid.page_title') }}
            </h1>
            <p class="mt-2 text-sm sm:text-base text-white/90 max-w-md mx-auto">
              {{ t('license.invalid.subtitle') }}
            </p>
          </div>

          <div class="px-5 py-6 sm:px-8 sm:py-8 space-y-5 sm:space-y-6">
            <!-- Alerta principal -->
            <div class="rounded-xl border border-danger-200 dark:border-danger-800 bg-danger-50 dark:bg-danger-900/20 p-4 sm:p-5">
              <div class="flex gap-3 sm:gap-4">
                <div class="flex-shrink-0 mt-0.5">
                  <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-danger-100 dark:bg-danger-900/50 text-danger-600 dark:text-danger-400">
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                <div class="min-w-0 flex-1">
                  <h2 class="text-base sm:text-lg font-semibold text-danger-800 dark:text-danger-200">
                    {{ presentation.title }}
                  </h2>
                  <p class="mt-1.5 text-sm sm:text-[15px] leading-relaxed text-danger-700 dark:text-danger-300 break-words">
                    {{ presentation.message }}
                  </p>
                  <p v-if="presentation.hint" class="mt-2 text-xs sm:text-sm text-danger-600 dark:text-danger-400">
                    {{ presentation.hint }}
                  </p>
                  <p
                    v-if="showTechnicalError"
                    class="mt-3 text-xs font-mono text-neutral-600 dark:text-neutral-400 break-all rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-900/60 px-3 py-2"
                  >
                    {{ technicalError }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Domínio + status -->
            <div class="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 p-4 sm:p-5">
              <h3 class="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mb-3">
                {{ t('license.invalid.domain_info') }}
              </h3>
              <dl class="space-y-3 text-sm">
                <div class="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <dt class="text-neutral-500 dark:text-neutral-400">{{ t('license.invalid.current_domain') }}</dt>
                  <dd class="font-mono text-sm font-semibold text-neutral-900 dark:text-white break-all sm:text-right">
                    {{ currentDomain }}
                  </dd>
                </div>
                <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <dt class="text-neutral-500 dark:text-neutral-400">{{ t('license.invalid.status') }}</dt>
                  <dd>
                    <span
                      class="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs sm:text-sm font-semibold"
                      :class="presentation.visual.badge"
                    >
                      <span class="h-2 w-2 rounded-full" :class="presentation.visual.dot" />
                      {{ presentation.badge }}
                    </span>
                  </dd>
                </div>
                <div v-if="licenseCodeLabel" class="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <dt class="text-neutral-500 dark:text-neutral-400">{{ t('license.invalid.reason_code') }}</dt>
                  <dd class="font-mono text-xs text-neutral-700 dark:text-neutral-300 break-all sm:text-right">
                    {{ licenseCodeLabel }}
                  </dd>
                </div>
              </dl>
            </div>

            <!-- Passos -->
            <div class="rounded-xl border border-primary-200 dark:border-primary-800/60 bg-primary-50 dark:bg-primary-900/15 p-4 sm:p-5">
              <h3 class="text-sm sm:text-base font-semibold text-primary-900 dark:text-primary-200 mb-3 flex items-center gap-2">
                <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {{ t('license.invalid.what_to_do') }}
              </h3>
              <ol class="space-y-3 text-sm text-neutral-600 dark:text-neutral-300">
                <li v-for="(step, index) in steps" :key="index" class="flex items-start gap-3">
                  <span class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">
                    {{ index + 1 }}
                  </span>
                  <span class="pt-0.5 leading-relaxed">{{ step }}</span>
                </li>
              </ol>
            </div>

            <!-- Recheck feedback -->
            <div
              v-if="recheckMessage"
              class="rounded-lg px-4 py-3 text-sm border"
              :class="recheckOk
                ? 'bg-success-50 text-success-800 dark:bg-success-900/20 dark:text-success-300 border-success-200 dark:border-success-800'
                : 'bg-neutral-50 text-neutral-700 dark:bg-neutral-800/80 dark:text-neutral-300 border-neutral-200 dark:border-neutral-600'"
            >
              {{ recheckMessage }}
            </div>

            <!-- Ações -->
            <div class="flex flex-col-reverse gap-3 sm:flex-row">
              <button
                type="button"
                class="btn-secondary flex-1 justify-center py-3 sm:py-3.5"
                :disabled="checking"
                @click="checkLicense"
              >
                <svg v-if="checking" class="animate-spin w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <svg v-else class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {{ checking ? t('license.invalid.checking') : t('license.invalid.recheck') }}
              </button>

              <a
                v-if="supportHref"
                :href="supportHref"
                target="_blank"
                rel="noopener noreferrer"
                class="btn-primary flex-1 justify-center py-3 sm:py-3.5"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                {{ t('license.invalid.contact_support') }}
              </a>
            </div>
          </div>

          <div class="border-t border-neutral-200 dark:border-neutral-800 px-5 py-4 sm:px-8 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm text-neutral-500 dark:text-neutral-400">
            <span><strong class="text-neutral-700 dark:text-neutral-300">{{ getPanelName }}</strong></span>
            <span>{{ t('license.invalid.managed_externally') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, h } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useBranding } from '@/composables/useBranding'
import { useThemeStore } from '@/stores/theme'
import { apiService } from '@/services/api'
import { resolveAppPath } from '@/utils/publicPath'
import { resolveLicensePresentation } from '@/utils/licenseStatus'

const { t } = useI18n()
const route = useRoute()
const themeStore = useThemeStore()
const { getPanelName, getLoginLogo, loadBranding } = useBranding()

const checking = ref(false)
const logoFailed = ref(false)
const recheckMessage = ref('')
const recheckOk = ref(false)
/** Estado actual da API (substitui query antiga da URL após verificação) */
const liveStatus = ref(null)

const isDark = computed(() => themeStore.isDark)

const urlParams = computed(() => new URLSearchParams(window.location.search))
const queryCode = computed(() => {
  if (liveStatus.value?.code) {
    return String(liveStatus.value.code)
  }
  return String(route.query.code || urlParams.value.get('code') || '')
})
const technicalError = computed(() => {
  if (liveStatus.value?.message) {
    return String(liveStatus.value.message)
  }
  return String(route.query.error || urlParams.value.get('error') || '').trim()
})
function normalizeLicenseSupportUrl(url) {
  const trimmed = String(url || '').trim()
  if (!trimmed) return ''
  return trimmed
    .replace(/\/v1\/license\/validate\/?$/i, '')
    .replace(/\/v1\/license\/?$/i, '')
    .replace(/\/license\/validate\/?$/i, '')
    .replace(/\/+$/, '')
}

const supportHref = computed(() => {
  const candidates = [
    liveStatus.value?.support_url,
    route.query.support_url,
    urlParams.value.get('support_url'),
  ]

  for (const candidate of candidates) {
    const normalized = normalizeLicenseSupportUrl(candidate)
    if (normalized) return normalized
  }

  return ''
})
const currentDomain = computed(() => {
  if (liveStatus.value?.domain) {
    return String(liveStatus.value.domain)
  }
  return String(route.query.domain || urlParams.value.get('domain') || window.location.hostname)
})

const presentation = computed(() =>
  resolveLicensePresentation({
    code: queryCode.value,
    errorMessage: technicalError.value,
    t,
    panelName: getPanelName.value,
  })
)

const licenseCodeLabel = computed(() => presentation.value.code)

const showTechnicalError = computed(() => {
  if (!technicalError.value) return false
  const normalizedMsg = presentation.value.message.trim().toLowerCase()
  return technicalError.value.trim().toLowerCase() !== normalizedMsg
})

const steps = computed(() => [
  t('license.invalid.step_1'),
  t('license.invalid.step_2'),
  t('license.invalid.step_3'),
])

const headerIcon = computed(() => {
  const icons = {
    clock: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24', class: 'h-9 w-9 sm:h-10 sm:w-10' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' }),
    ]),
    globe: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24', class: 'h-9 w-9 sm:h-10 sm:w-10' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z' }),
    ]),
    key: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24', class: 'h-9 w-9 sm:h-10 sm:w-10' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' }),
    ]),
    pause: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24', class: 'h-9 w-9 sm:h-10 sm:w-10' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z' }),
    ]),
    alert: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24', class: 'h-9 w-9 sm:h-10 sm:w-10' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' }),
    ]),
    shield: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24', class: 'h-9 w-9 sm:h-10 sm:w-10' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' }),
    ]),
  }
  return icons[presentation.value.visual.icon] || icons.shield
})

function toggleTheme() {
  themeStore.toggleTheme()
}

function applyLiveStatus(result) {
  if (!result || typeof result !== 'object') return
  liveStatus.value = {
    valid: !!result.valid,
    code: result.code || (result.valid ? 'active' : 'invalid'),
    message: result.message || '',
    domain: result.domain || window.location.hostname,
    support_url: normalizeLicenseSupportUrl(result.support_url),
  }
}

function redirectToPanelHome() {
  recheckOk.value = true
  recheckMessage.value = t('license.invalid.recheck_success')
  window.setTimeout(() => {
    window.location.replace(resolveAppPath('/'))
  }, 400)
}

async function checkLicense(options = {}) {
  const { silent = false, autoRedirect = true } = options
  if (!silent) {
    checking.value = true
    recheckMessage.value = ''
    recheckOk.value = false
  }

  try {
    const result = await apiService.license.status({ force: true, silent })
    applyLiveStatus(result)

    if (result?.valid) {
      if (autoRedirect) {
        redirectToPanelHome()
        return true
      }
      return true
    }

    if (!silent) {
      const detail = result?.message ? String(result.message) : ''
      recheckMessage.value = detail
        ? `${t('license.invalid.recheck_still_invalid')} (${detail})`
        : t('license.invalid.recheck_still_invalid')
    }
    return false
  } catch (err) {
    if (!silent) {
      const msg = err?.message ? String(err.message) : ''
      recheckMessage.value = msg
        ? `${t('license.invalid.recheck_failed')}: ${msg}`
        : t('license.invalid.recheck_failed')
    }
    return false
  } finally {
    if (!silent) {
      checking.value = false
    }
  }
}

onMounted(async () => {
  try {
    await loadBranding()
  } catch {
    /* branding opcional nesta rota */
  }

  // Sincroniza com o servidor: se a licença já está OK, sai desta página (URL pode ter code antigo)
  await checkLicense({ silent: true, autoRedirect: true })
})
</script>
